"use client";

import { c as _c } from "react/compiler-runtime";
import { captureException } from "@sentry/nextjs";
import { useActorRef } from "@xstate/react";
import { createContext, useEffect, useState } from "react";
import type { ActorRefFrom } from "xstate";
import { formatApiTime } from "@/app/components/DateAndTime/TimeList/TimeList.helper";
import type { DigitalVisitReasonEnum, SlotsSearchResponseV4 } from "@/lib/__generated/api";
import type { BffFacilityFieldsFragment } from "@/lib/__generated/beffe.sdk";
import { schedulerMachine } from "@/lib/machines/schedulerMachine";
import { sessionStorageHelper, SLOT_STORAGE_KEY } from "@/lib/utils/session-storage-utils";
import type { AppointmentData } from "@/types/scheduler-machine";
type SchedulerActorRef = ActorRefFrom<typeof schedulerMachine>;
function readPreselectedAppointment(facility: BffFacilityFieldsFragment | null): Partial<AppointmentData> | undefined {
  const slot = sessionStorageHelper.getKey<SlotsSearchResponseV4>(SLOT_STORAGE_KEY);
  if (!slot?.slotId || !slot.time) return undefined;
  return {
    slotId: slot.slotId,
    facilityCode: facility?.code,
    date: slot.time.slice(0, 10),
    rawTime: slot.time,
    time: formatApiTime(slot.time),
    // Location is backfilled by assignFacilityAndStateCode once facility resolves.
    location: facility?.name ?? "",
    leadScore: slot.patientValue ?? 0,
    leadTierNational: slot.nationalPatientTier ?? 0,
    leadTierOffice: slot.patientTier ?? 0
  };
}
interface SchedulerMachineProviderProps {
  facility: BffFacilityFieldsFragment | null;
  initialReason?: DigitalVisitReasonEnum;
  stateCode: string;
  restUrl: string;
  recaptchaSiteKey: string;
  enableRecaptcha?: boolean;
  disableExternalConfirmation?: boolean;
  reloadFacilityByCode?: (code: string) => Promise<void>;
  children: React.ReactNode;
}
export const SchedulerMachineContext = createContext<SchedulerActorRef | null>(null);
SchedulerMachineContext.displayName = "SchedulerMachineContext";
export function SchedulerMachineProvider(t0) {
  const $ = _c(27);
  const {
    facility,
    initialReason,
    stateCode,
    restUrl,
    recaptchaSiteKey,
    enableRecaptcha,
    disableExternalConfirmation,
    reloadFacilityByCode,
    children
  } = t0;
  let t1;
  if ($[0] !== facility) {
    t1 = () => readPreselectedAppointment(facility);
    $[0] = facility;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [preselectedAppointment] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== preselectedAppointment) {
    t2 = () => {
      if (preselectedAppointment) {
        sessionStorageHelper.removeKey(SLOT_STORAGE_KEY);
      }
    };
    t3 = [preselectedAppointment];
    $[2] = preselectedAppointment;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== disableExternalConfirmation || $[6] !== enableRecaptcha || $[7] !== facility || $[8] !== initialReason || $[9] !== preselectedAppointment || $[10] !== recaptchaSiteKey || $[11] !== restUrl || $[12] !== stateCode) {
    t4 = {
      input: {
        facility,
        initialReason,
        stateCode,
        restUrl,
        recaptchaSiteKey,
        enableRecaptcha,
        disableExternalConfirmation,
        preselectedAppointment
      }
    };
    $[5] = disableExternalConfirmation;
    $[6] = enableRecaptcha;
    $[7] = facility;
    $[8] = initialReason;
    $[9] = preselectedAppointment;
    $[10] = recaptchaSiteKey;
    $[11] = restUrl;
    $[12] = stateCode;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const actor = useActorRef(schedulerMachine, t4);
  const facilityCode = facility?.code ?? null;
  let t5;
  let t6;
  if ($[14] !== actor || $[15] !== facility || $[16] !== facilityCode || $[17] !== stateCode) {
    t5 = () => {
      const snapshotFacilityCode = actor.getSnapshot().context.facility?.code ?? null;
      const snapshotStateCode = actor.getSnapshot().context.stateCode;
      if (snapshotFacilityCode !== facilityCode || snapshotStateCode !== stateCode) {
        actor.send({
          type: "SYNC_FACILITY",
          facility,
          stateCode
        });
      }
    };
    t6 = [actor, facility, facilityCode, stateCode];
    $[14] = actor;
    $[15] = facility;
    $[16] = facilityCode;
    $[17] = stateCode;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[20] !== actor || $[21] !== reloadFacilityByCode) {
    t7 = () => {
      if (!reloadFacilityByCode) {
        return;
      }
      const sub = actor.on("FACILITY_SWAP_REQUESTED", t9 => {
        const {
          code
        } = t9;
        reloadFacilityByCode(code).catch(error => {
          captureException(error, {
            tags: {
              context: "facility_swap",
              swap_code: code
            }
          });
        });
      });
      return () => sub.unsubscribe();
    };
    t8 = [actor, reloadFacilityByCode];
    $[20] = actor;
    $[21] = reloadFacilityByCode;
    $[22] = t7;
    $[23] = t8;
  } else {
    t7 = $[22];
    t8 = $[23];
  }
  useEffect(t7, t8);
  let t9;
  if ($[24] !== actor || $[25] !== children) {
    t9 = <SchedulerMachineContext.Provider value={actor}>{children}</SchedulerMachineContext.Provider>;
    $[24] = actor;
    $[25] = children;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}